import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';

import { waitFor } from '@app/stores/utils/signal-async.utils';
import { AuthService } from '@auth/services/auth.service';
import { ClientAccess } from '@core-interactions/models/client-access';
import { ACCESS_STORE } from '@core-interactions/stores/access.store';
import { ENVIRONMENT } from '@env/environment';
import { RouterService } from '@shared/services/router.service';
import { APP_STORE } from '@stores/app.store';
import { Account } from '@stores/models/app/account';
import { AccountType } from '@stores/models/app/account-type';
import { User } from '@stores/models/user/user';
import { USER_STORE } from '@stores/user.store';

@Injectable()
export class AppInitializerService {
  readonly #authService = inject(AuthService);
  readonly #router = inject(Router);
  readonly #routerService = inject(RouterService);
  readonly #appStore = inject(APP_STORE);
  readonly #accessStore = inject(ACCESS_STORE);
  readonly #userStore = inject(USER_STORE);
  readonly #injector = inject(Injector);

  async init(): Promise<void> {
    await this.#authService.init();

    const [user] = await Promise.all([
      this.loadUserEnsured(),
      this.loadModulesEnsured()
    ]);

    const clientIdFromURL = this.getParamByName('clientId');
    let clientsIds: string[] = [];
    let selectedClientId: string | null = null;
    let redirectTo = this.#routerService.currentFullRoute;

    if (!user?.accounts?.length) {
      await this.#router.navigate(['/error'], {
        queryParams: { errorType: 'no-account' }
      });
      return;
    }

    const selectedAccount = this.selectAccount(user, clientIdFromURL);

    if (user.accounts.some(x => x.accountType === AccountType.SystemAdmin)) {
      clientsIds = (await this.loadClientsEnsured()).map(x => x.id);

      if (!clientsIds.length) {
        await this.#router.navigate(['/error'], {
          queryParams: { errorType: 'no-client' }
        });
        return;
      }

      selectedClientId = this.selectClient(clientsIds, clientIdFromURL);
    } else {
      if (
        !selectedAccount.client ||
        !selectedAccount.client.modules.includes(ENVIRONMENT.moduleId)
      ) {
        await this.#router.navigate(['/error'], {
          queryParams: { errorType: 'no-module-access' }
        });
        return;
      }

      clientsIds = user.accounts
        .map(x => x.client?.id)
        .filter((id): id is string => !!id);
      selectedClientId = selectedAccount.client?.id || null;
    }

    if (clientIdFromURL && !clientsIds.includes(clientIdFromURL)) {
      redirectTo = 'page-not-found';
    }

    await this.#router.navigate(['/client-setup'], {
      queryParams: {
        clientId: selectedClientId,
        reportId: this.getParamByName('reportId'),
        redirectTo
      }
    });
  }

  //#region Private methods

  private selectClient(
    clientsIds: string[],
    clientIdFromURL: string | null
  ): string {
    const clientId =
      clientsIds.find(x => x === clientIdFromURL) ||
      clientsIds.find(
        x => x === localStorage.getItem('lastSelectedClientId')
      ) ||
      clientsIds[0];

    this.#appStore.selectClient(clientId);

    return clientId;
  }

  private selectAccount(user: User, clientIdFromURL: string | null): Account {
    const account =
      user.accounts.find(x => x.client?.id === clientIdFromURL) ||
      user.accounts.find(
        x => x.client?.id === localStorage.getItem('lastSelectedClientId')
      ) ||
      user.accounts[0];

    this.#appStore.selectAccount(account.id, account.client?.id ?? '');

    return account;
  }

  private async loadUserEnsured(): Promise<User | null> {
    this.#userStore.loadUser();

    const state = await waitFor(
      this.#injector,
      () => ({
        loaded: this.#userStore.loaded(),
        data: this.#userStore.data(),
        error: this.#userStore.error()
      }),
      value => value.loaded === true
    );

    return state.data;
  }

  private async loadModulesEnsured(): Promise<void> {
    this.#accessStore.loadModules();

    await waitFor(
      this.#injector,
      () => this.#accessStore.modules().loaded,
      loaded => loaded
    );
  }

  private async loadClientsEnsured(): Promise<ClientAccess[]> {
    this.#accessStore.loadClients();

    await waitFor(
      this.#injector,
      () => this.#accessStore.clients().loaded,
      loaded => loaded
    );

    return this.#accessStore.clients().entities;
  }

  private getParamByName(name: string): string | null {
    const param = new URLSearchParams(window.location.search).get(name);
    if (param !== null) {
      return param;
    }

    const positions: Record<string, number> = {
      clientId: 1,
      reportId: 2
    };

    const position = positions[name];
    if (position === undefined) {
      return null;
    }

    // Ensure URL has clientId/reportId segments for correct handling on load/refresh.
    const pathSegments = window.location.pathname.split('/');
    if (pathSegments.length < 4) {
      return null;
    }

    const segment = pathSegments[position];
    return segment ?? null;
  }

  //#endregion
}
